import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon, Check, ArrowRight, ArrowLeft, Car, Truck, Crown, Info, Clock,
} from "lucide-react";
import { PACKAGES, ADDONS, AUTHORITY_PLUS_VEHICLE, BUSINESS } from "@/data/content";
import { BOOKING } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import PhotoCapture from "@/components/PhotoCapture";

const TIME_SLOTS = [
  "7:00 AM", "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

export default function Booking() {
  const [params] = useSearchParams();
  const initial = params.get("pkg") || "2nd_degree";

  const [pkg, setPkg] = useState(initial);
  const [vehicle, setVehicle] = useState("sedan");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [authorityPlus, setAuthorityPlus] = useState(false);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [slots, setSlots] = useState(null); // live availability for the selected date
  const [slotError, setSlotError] = useState("");
  const [photo, setPhoto] = useState(null); // optional quick photo of the vehicle

  const currentPkg = PACKAGES.find((p) => p.id === pkg);

  const fetchAvailability = useCallback((d, packageId) => {
    if (!d) { setSlots(null); return; }
    api
      .get("/availability", { params: { date: format(d, "yyyy-MM-dd"), booking_type: "detail", package: packageId } })
      .then(({ data }) => setSlots(data.slots))
      .catch(() => setSlots(null));
  }, []);

  // Check live availability as soon as a date (or package) is chosen
  useEffect(() => {
    fetchAvailability(date, pkg);
  }, [date, pkg, fetchAvailability]);

  // If the chosen time becomes unavailable (booked by someone else), clear it
  useEffect(() => {
    if (!slots || !time) return;
    const s = slots.find((x) => x.time === time);
    if (s && !s.available) {
      setTime("");
      setSlotError("That time is no longer available — please choose another.");
    }
  }, [slots, time]);

  // Authority+ only relevant after 2nd or 1st Degree
  useEffect(() => {
    if (!["2nd_degree", "1st_degree"].includes(pkg)) setAuthorityPlus(false);
  }, [pkg]);

  const { basePrice, addonBreakdown, total, hasVariable } = useMemo(() => {
    const base = currentPkg ? (vehicle === "sedan" ? currentPkg.sedan : currentPkg.suv) : 0;
    const items = selectedAddons
      .map((id) => ADDONS.find((a) => a.id === id))
      .filter(Boolean);
    const addonSum = items.reduce((s, a) => s + a.price, 0);
    const variable = items.some((a) => a.variable);
    return { basePrice: base, addonBreakdown: items, total: base + addonSum, hasVariable: variable };
  }, [currentPkg, vehicle, selectedAddons]);

  const toggleAddon = (id) => {
    setSelectedAddons((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Please pick a date.");
    if (!time) return toast.error("Please pick a time.");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        booking_type: "detail",
        vehicle_type: vehicle,
        package: pkg,
        addons: selectedAddons,
        authority_plus_vehicle_optin: authorityPlus,
        frequency: "one_time",
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        photo_url: photo?.status === "ready" ? photo.url : undefined,
      };
      const { data } = await api.post("/bookings", payload);
      setDone(data);
      toast.success("Booking request received.");
    } catch (err) {
      if (err.response?.status === 409) {
        // Someone else grabbed the slot first — tell the customer, never move them silently.
        const msg = formatApiErrorDetail(err.response.data?.detail);
        setSlotError(msg);
        setTime("");
        toast.error(msg);
        fetchAvailability(date, pkg);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit booking.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="min-h-screen pt-32 pb-24 px-5 lg:px-8 bg-[#050914]">
        <div className="max-w-2xl mx-auto text-center" data-testid={BOOKING.successMsg}>
          <div className="h-20 w-20 mx-auto rounded-full bg-blue-600/20 border border-blue-500/40 grid place-items-center mb-6">
            <Check className="h-10 w-10 text-blue-400" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Booking Request Received.</h1>
          <p className="mt-4 text-white/70">
            Thanks <span className="text-white font-semibold">{done.name}</span> — we have your request for the{" "}
            <span className="text-blue-400 font-semibold">{PACKAGES.find((p) => p.id === done.package)?.label}</span>{" "}
            on <span className="text-white">{done.preferred_date}</span> at{" "}
            <span className="text-white">{done.preferred_time}</span>.
          </p>
          {done.addons?.length > 0 && (
            <p className="mt-2 text-white/60 text-sm">
              Add-ons: {done.addons.map((id) => ADDONS.find((a) => a.id === id)?.name).filter(Boolean).join(" · ")}
            </p>
          )}
          <p className="mt-2 text-white/60">
            {done.variable_pricing ? "Starting at " : ""}
            <span className="text-white font-semibold">${done.estimated_price}</span>
            {done.variable_pricing && (
              <span className="block text-xs mt-1 text-white/40">Final pricing confirmed after inspection.</span>
            )}
          </p>
          {done.authority_plus_vehicle_optin && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-sm">
              <Crown className="h-4 w-4" /> Authority+ enrollment noted
            </div>
          )}
          <p className="mt-4 text-white/60 text-sm">
            <strong className="text-white">What happens next:</strong> we'll review your request and contact you at {done.phone} to confirm
            your appointment. You'll also receive an email copy of this request. Your appointment is not confirmed until we reach out.
          </p>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-md font-semibold transition-all">
            Back to Home <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-28 pb-24 px-5 lg:px-8 bg-[#050914]">
      <div className="max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <form onSubmit={submit} className="rounded-2xl glass p-6 sm:p-10 space-y-8">
            {/* Package */}
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Step 1</div>
              <h2 className="font-display text-2xl font-bold text-white">Choose Your Package</h2>
              <div className="mt-4 grid sm:grid-cols-3 gap-3">
                {PACKAGES.map((p) => (
                  <button
                    type="button" key={p.id} onClick={() => setPkg(p.id)}
                    className={`text-left p-4 rounded-xl border transition-all relative ${
                      pkg === p.id ? "border-blue-500 bg-blue-500/10" : "border-white/10 bg-white/5 hover:border-white/20"
                    }`}
                    data-testid={BOOKING.packageSelect(p.id)}
                  >
                    {p.popular && (
                      <div className="absolute -top-2 right-3 bg-blue-600 text-white text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">Most Popular</div>
                    )}
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-400">{p.tier}</div>
                    <div className="font-semibold text-white mt-1 leading-tight">{p.label}</div>
                    <div className="text-xs text-white/50 mt-2">${p.sedan} sedan · ${p.suv} SUV/truck</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Vehicle */}
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Step 2</div>
              <h2 className="font-display text-2xl font-bold text-white">Vehicle Type</h2>
              <div className="mt-4 grid grid-cols-2 gap-3 max-w-md">
                <button type="button" onClick={() => setVehicle("sedan")}
                  className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                    vehicle === "sedan" ? "border-blue-500 bg-blue-500/10" : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                  data-testid={BOOKING.vehicleSelect("sedan")}>
                  <Car className="h-5 w-5 text-blue-400" /><span className="text-white font-semibold">Sedan / Coupe</span>
                </button>
                <button type="button" onClick={() => setVehicle("suv_truck")}
                  className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                    vehicle === "suv_truck" ? "border-blue-500 bg-blue-500/10" : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                  data-testid={BOOKING.vehicleSelect("suv_truck")}>
                  <Truck className="h-5 w-5 text-blue-400" /><span className="text-white font-semibold">SUV / Truck</span>
                </button>
              </div>
            </div>

            {/* Add-ons */}
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Step 3 · Optional</div>
              <h2 className="font-display text-2xl font-bold text-white">Add-On Services</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {ADDONS.map((a) => {
                  const selected = selectedAddons.includes(a.id);
                  return (
                    <button
                      type="button" key={a.id} onClick={() => toggleAddon(a.id)}
                      className={`text-left p-4 rounded-xl border transition-all relative ${
                        selected ? "border-blue-500 bg-blue-500/10" : "border-white/10 bg-white/5 hover:border-white/20"
                      }`}
                      data-testid={`booking-addon-${a.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-white text-sm">{a.name}</div>
                        <div className="text-xs font-mono text-blue-400 whitespace-nowrap">{a.priceLabel}</div>
                      </div>
                      <div className="text-xs text-white/50 mt-2 leading-relaxed">{a.desc}</div>
                      <div className={`absolute top-3 right-3 h-5 w-5 rounded-full border flex items-center justify-center transition-all ${
                        selected ? "bg-blue-500 border-blue-500" : "border-white/30 opacity-0"
                      }`}>
                        {selected && <Check className="h-3 w-3 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional quick photo */}
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Step 4 · Optional</div>
              <PhotoCapture
                idPrefix="booking"
                heading="Send us a quick photo of your vehicle."
                subtext="Upload a photo so we can get a better idea of your vehicle's visible condition and help us prepare for your service."
                value={photo}
                onChange={setPhoto}
              />
            </div>

            {/* Date/time */}
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Step 5</div>
              <h2 className="font-display text-2xl font-bold text-white">Pick a Date & Time</h2>
              <div className="mt-4">
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="w-full sm:w-72 text-left flex items-center gap-3 bg-white/5 border border-white/10 text-white rounded-md h-12 px-4 hover:border-white/20" data-testid={BOOKING.dateInput}>
                      <CalendarIcon className="h-4 w-4 text-blue-400" />
                      {date ? format(date, "EEE, MMM d, yyyy") : "Pick a date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#0B132B] border-white/10">
                    <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setSlotError(""); }} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} className="text-white" />
                  </PopoverContent>
                </Popover>

                <div className="mt-4">
                  {!date ? (
                    <div className="rounded-xl border border-dashed border-white/15 bg-white/5 h-12 flex items-center gap-2 px-4 text-sm text-white/40" data-testid={BOOKING.timeSelect}>
                      <Clock className="h-4 w-4" /> Pick a date to see live availability
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={BOOKING.timeSelect}>
                      {TIME_SLOTS.map((t) => {
                        const s = slots?.find((x) => x.time === t);
                        const unavailable = slots ? !s?.available : false;
                        const active = time === t;
                        return (
                          <button
                            type="button" key={t}
                            disabled={unavailable}
                            onClick={() => { setTime(t); setSlotError(""); }}
                            title={unavailable ? (s?.reason === "taken" ? "Already booked" : "Not enough time left in the day for this package") : `Arrive at ${t}`}
                            className={`h-12 rounded-lg border text-sm font-semibold transition-all ${
                              active
                                ? "border-blue-500 bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                                : unavailable
                                  ? "border-white/5 bg-white/[0.02] text-white/25 line-through cursor-not-allowed"
                                  : "border-white/10 bg-white/5 text-white/80 hover:border-blue-500/50 hover:text-white"
                            }`}
                            data-testid={`booking-slot-${t.replace(/[: ]/g, "-").toLowerCase()}`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {slotError && (
                    <p className="mt-3 text-sm text-red-400 font-medium" data-testid="booking-slot-error">{slotError}</p>
                  )}
                  <p className="mt-3 text-xs text-white/40">
                    Times are arrival windows. Your appointment is confirmed when we contact you — submitting this form sends a request.
                  </p>
                </div>
              </div>
            </div>

            {/* Details */}
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Step 6</div>
              <h2 className="font-display text-2xl font-bold text-white">Your Details</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name" className="bg-white/5 border-white/10 text-white h-12" data-testid={BOOKING.name} />
                <Input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="bg-white/5 border-white/10 text-white h-12" data-testid={BOOKING.phone} />
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-white/5 border-white/10 text-white h-12 sm:col-span-2" data-testid={BOOKING.email} />
                <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Service Address" className="bg-white/5 border-white/10 text-white h-12 sm:col-span-2" data-testid={BOOKING.address} />
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (vehicle condition, gate codes, special requests...)" rows={4} className="bg-white/5 border-white/10 text-white sm:col-span-2" data-testid={BOOKING.notes} />
              </div>
            </div>

            {/* Authority+ upsell — for 2nd or 1st Degree only */}
            {["2nd_degree", "1st_degree"].includes(pkg) && (
              <div className="rounded-2xl p-6 border border-emerald-500/30 bg-emerald-500/5">
                <div className="flex items-start gap-3 mb-3">
                  <Crown className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">Authority+ Optional</div>
                    <h3 className="font-display text-lg font-bold text-white mt-1">Keep it maintained with Authority+</h3>
                  </div>
                </div>
                <p className="text-sm text-white/70 leading-relaxed">
                  After this detail, continue at exclusive member pricing:{" "}
                  <span className="text-emerald-400 font-semibold">${AUTHORITY_PLUS_VEHICLE.sedan}/visit</span> sedan ·{" "}
                  <span className="text-emerald-400 font-semibold">${AUTHORITY_PLUS_VEHICLE.suv}/visit</span> SUV/truck
                  (vs. regular ${AUTHORITY_PLUS_VEHICLE.regularSedan} / ${AUTHORITY_PLUS_VEHICLE.regularSuv}).
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <button type="button" onClick={() => setAuthorityPlus(true)}
                    className={`text-left p-3 rounded-xl border text-sm font-semibold transition-all ${
                      authorityPlus ? "border-emerald-500 bg-emerald-500/10 text-white" : "border-white/10 bg-white/5 text-white/80 hover:border-white/20"
                    }`}
                    data-testid="booking-authority-plus-yes">
                    Yes, add Authority+
                  </button>
                  <button type="button" onClick={() => setAuthorityPlus(false)}
                    className={`text-left p-3 rounded-xl border text-sm font-semibold transition-all ${
                      !authorityPlus ? "border-white/40 bg-white/10 text-white" : "border-white/10 bg-white/5 text-white/80 hover:border-white/20"
                    }`}
                    data-testid="booking-authority-plus-no">
                    No, thank you
                  </button>
                </div>
              </div>
            )}

            {/* Customer requirements — visible before booking */}
            <div className="rounded-2xl p-5 border-2 border-amber-500/30 bg-amber-500/5">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400 mb-2">Customer Requirements</div>
              <p className="text-sm text-white/80 leading-relaxed">
                For the time being, Authority Detailing Co. requires access to a <strong className="text-white">water hookup</strong> and{" "}
                <strong className="text-white">electrical outlet</strong> at the service location. Please make sure both are accessible before your scheduled service.
              </p>
            </div>

            <button type="submit" disabled={submitting || photo?.status === "uploading"}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold py-4 rounded-md transition-all shadow-lg shadow-blue-600/30"
              data-testid={BOOKING.submit}>
              {submitting ? "Submitting..." : photo?.status === "uploading" ? "Attaching photo…" : "Book Your Service"}
            </button>
          </form>

          <aside className="lg:sticky lg:top-28 self-start rounded-2xl glass p-6 space-y-4 h-fit">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-blue-400">Booking Summary</div>
            <div className="text-white">
              <div className="text-sm text-white/60">{currentPkg?.tier}</div>
              <div className="font-display text-lg font-semibold leading-tight">{currentPkg?.label}</div>
            </div>
            <div className="text-xs text-white/60">
              {vehicle === "sedan" ? "Sedan / Coupe" : "SUV / Truck"} · {currentPkg?.time}
            </div>

            <div className="border-t border-white/10 pt-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-white/70"><span>Base price</span><span>${basePrice}</span></div>
              {addonBreakdown.map((a) => (
                <div key={a.id} className="flex justify-between text-white/70 text-xs">
                  <span className="truncate pr-2">{a.name}{a.variable ? " *" : ""}</span>
                  <span>+${a.price}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-4">
              <div className="text-xs uppercase tracking-[0.2em] text-white/50">
                {hasVariable ? "Starting At" : "Estimated Total"}
              </div>
              <div className="font-display text-4xl font-bold text-white mt-1" data-testid={BOOKING.estimate}>${total}</div>
              {hasVariable && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] text-white/50">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>* Final pricing confirmed after inspection.</span>
                </div>
              )}
            </div>

            {authorityPlus && (
              <div className="border-t border-white/10 pt-4">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
                  <Crown className="h-3 w-3" /> Authority+ Noted
                </div>
                <div className="text-xs text-white/60 mt-2 leading-relaxed">
                  Recurring maintenance visits at ${vehicle === "sedan" ? AUTHORITY_PLUS_VEHICLE.sedan : AUTHORITY_PLUS_VEHICLE.suv}/visit after this detail.
                </div>
              </div>
            )}

            <div className="border-t border-white/10 pt-4 text-xs text-white/60">
              Questions? Call <a href={`tel:${BUSINESS.phoneRaw}`} className="text-blue-400 font-semibold">{BUSINESS.phone}</a>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
